import React, { useCallback, useMemo, useState } from 'react';
import { getDroppedLineCount } from '@features/encounters/utils/billingClaim';
import { getBulkCosignItemErrorMessage } from '@features/note-ai-edit/utils/signingErrors';
import { Avatar, ProviderBadge } from '@jotpsych/ui';
import { Colors } from '@jotpsych/ui/theme';
import {
  CalendarTodayOutlined,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  DescriptionOutlined,
  FactCheckOutlined,
  KeyboardArrowDown,
  KeyboardArrowUp,
  LocalOfferOutlined,
  Search,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Paper,
  Skeleton,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useEventTracking } from '@services/statsig';
import { displayCalendarDate, parseCalendarDate } from '@shared/dates/calendarDate';
import type { NoteListItemResponse } from '@store/generated/notes';
import { useSnackbar } from 'notistack';

import { useCosignDisposition } from '../hooks/useCosignDisposition';
import { useNoteQueueList } from '../hooks/useNoteQueueList';
import { useBulkCosignMutation } from '../store/api';

const palette = {
  ink: Colors.eggplantNeutral900,
  muted: Colors.eggplantNeutral600,
  canvas: Colors.eggplantNeutral100,
  surface: Colors.brand_white,
  line: Colors.eggplantNeutral200,
  brand: Colors.primary700,
  brandHover: Colors.primary800,
  brandWash: Colors.primary100,
  brandBorder: Colors.primary200,
};

function initials(name?: string | null) {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  return (
    parts
      .slice(0, 2)
      .map(part => part[0]?.toUpperCase())
      .join('') || '—'
  );
}

function formatServiceDate(value?: string | null) {
  if (!value) return 'Service date unavailable';
  const parsed = parseCalendarDate(value);
  if (!parsed) return value;
  return displayCalendarDate(parsed, undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatSignedAt(value?: string | null) {
  if (!value) return 'Signing time unavailable';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(parsed);
}

function EvidenceHeader({
  icon,
  title,
  trailing,
}: {
  icon: React.ReactNode;
  title: string;
  trailing?: React.ReactNode;
}) {
  return (
    <Stack direction="row" sx={{ mb: 1.1, alignItems: 'center', justifyContent: 'space-between' }}>
      <Stack direction="row" spacing={0.8} sx={{ alignItems: 'center' }}>
        <Box sx={{ color: palette.muted, display: 'flex', '& svg': { fontSize: 17 } }}>{icon}</Box>
        <Typography
          sx={{
            color: palette.ink,
            fontSize: 12,
            fontWeight: 750,
            letterSpacing: '0.055em',
            textTransform: 'uppercase',
          }}
        >
          {title}
        </Typography>
      </Stack>
      {trailing}
    </Stack>
  );
}

function BillingFact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Box>
      <Typography sx={{ color: palette.muted, fontSize: 11.5, mb: 0.2 }}>{label}</Typography>
      <Typography sx={{ color: palette.ink, fontSize: 13, fontWeight: 650 }}>{children}</Typography>
    </Box>
  );
}

function ReviewCard({
  note,
  selected,
  onToggle,
  onOpen,
  onDismiss,
  onSnooze,
  dispositionDisabled,
  failureMessage,
}: {
  note: NoteListItemResponse;
  selected: boolean;
  onToggle: () => void;
  onOpen: () => void;
  onDismiss: () => void;
  onSnooze: () => void;
  dispositionDisabled: boolean;
  /** Why the last bulk co-sign left this note unsigned, when the backend said. */
  failureMessage?: string | null;
}) {
  const [expanded, setExpanded] = useState(false);
  const authorSignature =
    note.signature_metadata?.find(signature => signature.role === 'author') ??
    note.signature_metadata?.[0];
  const authorName = authorSignature?.name || note.issuing_user_name || 'Unknown provider';
  const previewSections = note.preview_sections?.length
    ? note.preview_sections
    : note.summary
      ? [{ title: '', text: note.summary }]
      : [];
  const previewCharacterCount = previewSections.reduce(
    (count, section) => count + (section.title?.length ?? 0) + (section.text?.length ?? 0),
    0,
  );
  const isPreviewTruncatable = previewSections.length > 1 || previewCharacterCount > 320;
  const billing = note.billing_details;
  const procedureCodes =
    billing?.line_items?.map(line => {
      const modifierText = line.modifiers?.length ? `-${line.modifiers.join('-')}` : '';
      return `${line.code}${modifierText}`;
    }) ?? [];
  const totalCharge = billing?.line_items?.reduce(
    (total, line) => total + (line.charge_cents ?? 0),
    0,
  );
  // Lines the backend dropped from the read payload are missing from this
  // sum, so flag the total instead of presenting it as authoritative
  // (GDN-2658; signal lands with GDN-2657).
  const droppedLineCount = getDroppedLineCount(billing);

  return (
    // GDN-3288: the failure alert sits ABOVE the card, outside the Paper — the
    // Paper's maxHeight/overflow math is tuned to its own grid + footer, and an
    // alert inside it would clip the footer controls the failed note needs.
    <Stack spacing={1}>
      {failureMessage ? (
        <Alert
          severity="error"
          sx={{ borderRadius: 2, border: '1px solid', borderColor: 'error.light', typography: 'body2' }}
        >
          {failureMessage}
        </Alert>
      ) : null}
    <Paper
      component="article"
      elevation={0}
      sx={{
        overflow: 'hidden',
        maxHeight: {
          xs: 'none',
          md: expanded ? 560 : 440,
        },
        border: '1px solid',
        borderColor: selected ? palette.brand : palette.line,
        borderRadius: 2.5,
        boxShadow: selected
          ? `0 0 0 2px ${alpha(palette.brand, 0.11)}`
          : '0 1px 2px rgba(23,50,77,.03)',
        transition: 'border-color 150ms ease, box-shadow 150ms ease',
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'minmax(260px, .8fr) minmax(0, 2.2fr)' },
        }}
      >
        <Box
          sx={{
            p: { xs: 2, md: 2.25 },
            bgcolor: selected ? alpha(palette.brand, 0.035) : palette.surface,
            borderRight: { md: `1px solid ${palette.line}` },
            borderBottom: { xs: `1px solid ${palette.line}`, md: 0 },
          }}
        >
          <Stack direction="row" spacing={1.25} sx={{ minWidth: 0 }}>
            <Avatar
              id={`patient-${note.patient_id ?? note.note_id}`}
              sx={{ width: 38, height: 38, fontSize: 13, fontWeight: 750 }}
            >
              {initials(note.patient_name)}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ color: palette.ink, fontSize: 15.5, fontWeight: 760 }}>
                {note.patient_name || 'Patient not assigned'}
              </Typography>
              <Typography sx={{ color: palette.muted, fontSize: 12.5 }}>
                {note.note_title || note.template_name || 'Clinical note'}
              </Typography>
            </Box>
          </Stack>

          <Stack spacing={1.1} sx={{ mt: 2 }}>
            <Stack direction="row" spacing={0.8} sx={{ alignItems: 'center' }}>
              <CalendarTodayOutlined sx={{ color: palette.muted, fontSize: 15 }} />
              <Typography sx={{ color: palette.ink, fontSize: 12.5, fontWeight: 600 }}>
                {formatServiceDate(note.service_date)}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={0.8} sx={{ alignItems: 'center' }}>
              <LocalOfferOutlined sx={{ color: palette.muted, fontSize: 15 }} />
              <Typography sx={{ color: palette.ink, fontSize: 12.5 }}>
                {note.template_name || 'Clinical note'}
              </Typography>
            </Stack>
          </Stack>

          <Divider sx={{ my: 1.8, borderColor: palette.line }} />

          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <ProviderBadge name={authorName} size="regular" ariaLabel={`Signed by ${authorName}`} />
            <Box>
              <Typography
                sx={{
                  color: palette.muted,
                  fontSize: 10.5,
                  fontWeight: 750,
                  letterSpacing: '.045em',
                  textTransform: 'uppercase',
                }}
              >
                Signed by
              </Typography>
              <Typography sx={{ color: palette.ink, fontSize: 12.5, fontWeight: 700 }}>
                {authorName}
              </Typography>
              <Typography sx={{ color: palette.muted, fontSize: 11.5 }}>
                {formatSignedAt(authorSignature?.signed_at)}
              </Typography>
            </Box>
          </Stack>
        </Box>

        <Box sx={{ minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', lg: '1.08fr .92fr' },
              minHeight: 0,
              maxHeight: {
                xs: 'none',
                md: expanded ? 501 : 381,
              },
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                p: { xs: 2, md: 2.25 },
                minWidth: 0,
                minHeight: 0,
                overflow: 'hidden',
                borderRight: { lg: `1px solid ${palette.line}` },
              }}
            >
              <EvidenceHeader
                icon={<DescriptionOutlined />}
                title="Signed note"
                trailing={
                  <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                    {isPreviewTruncatable && (
                      <Button
                        size="small"
                        endIcon={expanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                        onClick={() => setExpanded(value => !value)}
                        aria-expanded={expanded}
                        sx={{
                          color: palette.brand,
                          minWidth: 0,
                          minHeight: 28,
                          px: 0.5,
                          fontSize: 11.5,
                          lineHeight: 1,
                        }}
                      >
                        {expanded ? 'Show less' : 'Show more'}
                      </Button>
                    )}
                    <Button
                      size="small"
                      onClick={onOpen}
                      sx={{
                        color: palette.brand,
                        minHeight: 28,
                        fontSize: 11.5,
                        lineHeight: 1,
                      }}
                    >
                      Full note
                    </Button>
                  </Stack>
                }
              />
              {previewSections.length ? (
                <Box
                  sx={{
                    position: 'relative',
                    maxHeight: expanded ? 270 : 132,
                    overflowY: expanded ? 'auto' : 'hidden',
                    pr: expanded ? 0.75 : 0,
                    scrollbarWidth: 'thin',
                    '&::after':
                      !expanded && isPreviewTruncatable
                        ? {
                            content: '""',
                            position: 'absolute',
                            inset: 'auto 0 0',
                            height: 42,
                            pointerEvents: 'none',
                            background: `linear-gradient(180deg, ${alpha(
                              palette.surface,
                              0,
                            )} 0%, ${palette.surface} 88%)`,
                          }
                        : undefined,
                  }}
                >
                  <Stack spacing={1.1}>
                    {previewSections.map((section, index) => (
                      <Box key={`${section.title}-${index}`}>
                        {section.title && (
                          <Typography sx={{ color: palette.ink, fontSize: 12.5, fontWeight: 700 }}>
                            {section.title}
                          </Typography>
                        )}
                        <Typography
                          sx={{
                            color: Colors.eggplantNeutral700,
                            fontSize: 13,
                            lineHeight: 1.55,
                            whiteSpace: 'pre-wrap',
                          }}
                        >
                          {section.text}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              ) : (
                <Typography sx={{ color: palette.muted, fontSize: 13 }}>
                  Preview unavailable. Open the full note to review.
                </Typography>
              )}

              <Divider sx={{ my: 1.8, borderColor: palette.line }} />
              <EvidenceHeader icon={<FactCheckOutlined />} title="Encounter analysis" />
              <Typography
                sx={{
                  color: Colors.eggplantNeutral700,
                  fontSize: 13,
                  lineHeight: 1.55,
                  display: '-webkit-box',
                  WebkitBoxOrient: 'vertical',
                  WebkitLineClamp: expanded ? 4 : 3,
                  overflow: 'hidden',
                }}
              >
                {note.encounter_analysis_summary ||
                  'No encounter analysis summary is available for this note.'}
              </Typography>
              {!!note.encounter_analysis_tags?.length && (
                <Stack direction="row" spacing={0.7} useFlexGap sx={{ mt: 1.2, flexWrap: 'wrap' }}>
                  {note.encounter_analysis_tags.map(tag => (
                    <Chip
                      key={tag}
                      size="small"
                      icon={<CheckCircle />}
                      label={tag}
                      sx={{
                        color: palette.brand,
                        bgcolor: palette.brandWash,
                        border: `1px solid ${palette.brandBorder}`,
                        '& .MuiChip-icon': { color: palette.brand, fontSize: 14 },
                      }}
                    />
                  ))}
                </Stack>
              )}
            </Box>

            <Box
              sx={{
                p: { xs: 2, md: 2.25 },
                minWidth: 0,
                overflow: 'hidden',
                bgcolor: alpha(palette.canvas, 0.45),
              }}
            >
              <EvidenceHeader icon={<FactCheckOutlined />} title="Billing details" />
              {billing ? (
                <Stack spacing={1.5}>
                  {droppedLineCount > 0 && (
                    <Typography sx={{ color: 'warning.dark', fontSize: 12 }}>
                      Some billing lines could not be displayed. The charge total may be
                      incomplete.
                    </Typography>
                  )}
                  <BillingFact label="Procedure">
                    {procedureCodes.length ? procedureCodes.join(', ') : 'Not specified'}
                  </BillingFact>
                  <BillingFact label="Diagnoses">
                    {billing.diagnosis_codes?.length
                      ? billing.diagnosis_codes.join(', ')
                      : 'Not specified'}
                  </BillingFact>
                  <Stack direction="row" spacing={3}>
                    <BillingFact label="Place of service">
                      {billing.place_of_service || 'Not specified'}
                    </BillingFact>
                    {totalCharge ? (
                      <BillingFact label="Charge">
                        {(totalCharge / 100).toLocaleString(undefined, {
                          style: 'currency',
                          currency: 'USD',
                        })}
                      </BillingFact>
                    ) : null}
                  </Stack>
                </Stack>
              ) : (
                <Typography sx={{ color: palette.muted, fontSize: 13, lineHeight: 1.55 }}>
                  No billing details are available for this encounter.
                </Typography>
              )}
            </Box>
          </Box>

          <Divider sx={{ borderColor: palette.line }} />
          <Box
            sx={{
              flexShrink: 0,
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', lg: '1.08fr .92fr' },
              minHeight: 56,
            }}
          >
            <Stack
              direction="row"
              spacing={0.5}
              sx={{
                minWidth: 0,
                alignItems: 'center',
                px: { xs: 2, md: 2.25 },
                minHeight: 56,
                borderRight: { lg: `1px solid ${palette.line}` },
                borderBottom: { xs: `1px solid ${palette.line}`, lg: 0 },
              }}
            >
              <Button
                size="small"
                color="inherit"
                disabled={dispositionDisabled}
                onClick={onSnooze}
                sx={{ height: 40, py: 0, lineHeight: '20px' }}
              >
                Remind tomorrow
              </Button>
              <Button
                size="small"
                color="inherit"
                disabled={dispositionDisabled}
                onClick={onDismiss}
                sx={{ height: 40, py: 0, lineHeight: '20px' }}
              >
                Dismiss
              </Button>
            </Stack>
            <Box
              sx={{
                minWidth: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: { xs: 'flex-start', lg: 'flex-end' },
                px: { xs: 2, md: 2.25 },
                minHeight: 56,
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={selected}
                    onChange={onToggle}
                    sx={{
                      alignSelf: 'center',
                      width: 40,
                      height: 40,
                      p: 0,
                    }}
                  />
                }
                label={
                  <Typography
                    sx={{
                      color: palette.ink,
                      fontSize: 13,
                      fontWeight: 700,
                      lineHeight: '20px',
                    }}
                  >
                    Reviewed and ready to sign
                  </Typography>
                }
                sx={{
                  m: 0,
                  height: 40,
                  alignItems: 'center',
                  '& .MuiFormControlLabel-label': {
                    display: 'flex',
                    height: 40,
                    alignItems: 'center',
                  },
                }}
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </Paper>
    </Stack>
  );
}

export function CosignReviewQueue({ organizationId }: { organizationId: string }) {
  const { enqueueSnackbar } = useSnackbar();
  const { note: noteMetrics } = useEventTracking();
  const [showDismissed, setShowDismissed] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  // GDN-3288: per-note reason the last bulk co-sign left it unsigned, for the
  // codes whose backend message is user-facing (PDF render / compliance
  // unavailable). Keyed by note_id; shown inline on the card.
  const [bulkFailureMessages, setBulkFailureMessages] = useState<Record<string, string>>({});
  const { dismiss, remindTomorrow, isUpdating } = useCosignDisposition();
  const [bulkCosign, { isLoading: isSigning }] = useBulkCosignMutation();
  const {
    items,
    isLoading,
    isFetching,
    totalCount,
    totalPages,
    page,
    setPage,
    searchTerm,
    setSearchTerm,
    navigateToItem,
    hasNextPage,
    hasPreviousPage,
    isEmpty,
    isSearchActive,
  } = useNoteQueueList({
    completionStatus: 'ready',
    organizationId,
    cosignScope: 'mine',
    includeDismissed: showDismissed,
  });

  const allPageSelected = items.length > 0 && items.every(item => selectedIds.has(item.note_id));
  const selectedCount = selectedIds.size;

  const toggleNote = useCallback(
    (noteId: string) => {
      setSelectedIds(current => {
        const next = new Set(current);
        if (next.has(noteId)) next.delete(noteId);
        else next.add(noteId);
        noteMetrics?.cosignBulkSelectionChanged?.(next.size);
        return next;
      });
    },
    [noteMetrics],
  );

  const removeSelectedNote = useCallback(
    (noteId: string) => {
      setBulkFailureMessages(current =>
        noteId in current
          ? Object.fromEntries(Object.entries(current).filter(([id]) => id !== noteId))
          : current,
      );
      setSelectedIds(current => {
        if (!current.has(noteId)) return current;
        const next = new Set(current);
        next.delete(noteId);
        noteMetrics?.cosignBulkSelectionChanged?.(next.size);
        return next;
      });
    },
    [noteMetrics],
  );

  const handleDismiss = useCallback(
    async (noteId: string) => {
      await dismiss(noteId);
      removeSelectedNote(noteId);
    },
    [dismiss, removeSelectedNote],
  );

  const handleSnooze = useCallback(
    async (noteId: string) => {
      await remindTomorrow(noteId);
      removeSelectedNote(noteId);
    },
    [remindTomorrow, removeSelectedNote],
  );

  const togglePage = useCallback(() => {
    setSelectedIds(current => {
      const next = new Set(current);
      if (allPageSelected) items.forEach(item => next.delete(item.note_id));
      else items.forEach(item => next.add(item.note_id));
      noteMetrics?.cosignBulkSelectionChanged?.(next.size);
      return next;
    });
  }, [allPageSelected, items, noteMetrics]);

  const handleBulkSign = useCallback(async () => {
    const noteIds = [...selectedIds];
    if (!noteIds.length) return;
    noteMetrics?.cosignBulkSubmitted?.(noteIds.length);
    try {
      const result = await bulkCosign({
        bulkCosignRequest: { note_ids: noteIds },
      }).unwrap();
      noteMetrics?.cosignBulkCompleted?.(result.signed_count, result.failed_count);
      const failedItems = (result.results ?? []).filter(item => !item.success);
      const failedIds = new Set(failedItems.map(item => item.note_id));
      setSelectedIds(failedIds);
      // Merge: only the notes in THIS request get their failure state
      // replaced (cleared on success, set on a user-facing failure); notes
      // not in the request keep whatever the previous attempt reported.
      const requested = new Set(noteIds);
      setBulkFailureMessages(current => {
        const next = Object.fromEntries(
          Object.entries(current).filter(([id]) => !requested.has(id)),
        );
        failedItems.forEach(item => {
          const message = getBulkCosignItemErrorMessage(item);
          if (message) next[item.note_id] = message;
        });
        return next;
      });
      if (result.failed_count) {
        // GDN-2784: future-dated encounters block co-signing until reviewed
        // note-by-note (the acknowledge decision is per note, not bulk).
        const allFutureDos =
          failedItems.length > 0 &&
          failedItems.every(item => item.error_code === 'future_date_of_service');
        enqueueSnackbar(
          allFutureDos
            ? `${result.signed_count} signed; ${result.failed_count} dated in the future — open each note to review its date of service before co-signing.`
            : `${result.signed_count} signed; ${result.failed_count} could not be signed and remain selected.`,
          { variant: 'warning' },
        );
      } else {
        enqueueSnackbar(
          `${result.signed_count} ${result.signed_count === 1 ? 'note' : 'notes'} co-signed.`,
          { variant: 'success' },
        );
      }
    } catch {
      // Transport failure: nothing per-note was learned, keep prior alerts.
      noteMetrics?.cosignBulkCompleted?.(0, noteIds.length);
      enqueueSnackbar('The selected notes could not be co-signed. Please try again.', {
        variant: 'error',
      });
    }
  }, [bulkCosign, enqueueSnackbar, noteMetrics, selectedIds]);

  const selectionLabel = useMemo(
    () => `${selectedCount} ${selectedCount === 1 ? 'note' : 'notes'} reviewed`,
    [selectedCount],
  );

  return (
    <Box sx={{ pb: 10 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        sx={{
          mb: 2.5,
          alignItems: { xs: 'stretch', sm: 'flex-start' },
          justifyContent: 'space-between',
        }}
      >
        <Box>
          <Typography variant="h5" sx={{ color: palette.ink, fontWeight: 760 }}>
            Co-sign review
          </Typography>
          <Typography sx={{ color: palette.muted, fontSize: 13.5, mt: 0.35 }}>
            Review the signed note, encounter analysis, and billing details before co-signing.
          </Typography>
        </Box>
        <Button
          variant="contained"
          disabled={!selectedCount || isSigning}
          onClick={() => void handleBulkSign()}
          startIcon={isSigning ? <CircularProgress size={16} color="inherit" /> : <CheckCircle />}
          sx={{
            bgcolor: palette.brand,
            '&:hover': { bgcolor: palette.brandHover },
            minWidth: 190,
            boxShadow: 'none',
          }}
        >
          {isSigning
            ? 'Signing…'
            : `Sign ${selectedCount || ''} reviewed ${selectedCount === 1 ? 'note' : 'notes'}`}
        </Button>
      </Stack>

      <Paper
        elevation={0}
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'stretch', md: 'center' },
          gap: 1.5,
          p: 1.5,
          mb: 2,
          border: `1px solid ${palette.line}`,
          borderRadius: 2.5,
        }}
      >
        <TextField
          fullWidth
          size="small"
          placeholder="Search patient, provider, or note"
          value={searchTerm}
          onChange={event => setSearchTerm(event.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: palette.muted }} />
                </InputAdornment>
              ),
            },
          }}
        />
        <FormControlLabel
          control={<Checkbox checked={allPageSelected} onChange={togglePage} />}
          label={<Typography sx={{ whiteSpace: 'nowrap', fontSize: 13 }}>Select page</Typography>}
          sx={{ m: 0 }}
        />
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={showDismissed}
              onChange={event => {
                setShowDismissed(event.target.checked);
                noteMetrics?.cosignShowDismissedToggled?.(event.target.checked);
              }}
            />
          }
          label={
            <Typography sx={{ whiteSpace: 'nowrap', fontSize: 13 }}>Show dismissed</Typography>
          }
          sx={{ m: 0 }}
        />
      </Paper>

      {!isLoading && (
        <Stack direction="row" sx={{ mb: 1.25, px: 0.5, justifyContent: 'space-between' }}>
          <Typography sx={{ color: palette.muted, fontSize: 12.5 }}>
            {totalCount.toLocaleString()} {totalCount === 1 ? 'note' : 'notes'} awaiting review
          </Typography>
          <Typography
            sx={{
              color: selectedCount ? palette.brand : palette.muted,
              fontSize: 12.5,
              fontWeight: 700,
            }}
          >
            {selectionLabel}
          </Typography>
        </Stack>
      )}

      {isLoading && !items.length ? (
        <Stack spacing={1.5}>
          {Array.from({ length: 3 }).map((_, index) => (
            <Skeleton key={index} variant="rounded" height={320} sx={{ borderRadius: 2.5 }} />
          ))}
        </Stack>
      ) : isEmpty ? (
        <Paper
          elevation={0}
          sx={{ textAlign: 'center', py: 8, px: 3, border: `1px solid ${palette.line}` }}
        >
          <Typography color="text.secondary">
            {isSearchActive
              ? `No notes found for “${searchTerm}”.`
              : 'No notes are awaiting your co-signature.'}
          </Typography>
        </Paper>
      ) : (
        <Box sx={{ position: 'relative' }}>
          {isFetching && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                zIndex: 2,
                bgcolor: alpha(palette.canvas, 0.35),
                pointerEvents: 'none',
              }}
            />
          )}
          <Stack spacing={1.5}>
            {items.map(note => (
              <ReviewCard
                key={note.note_id}
                note={note}
                selected={selectedIds.has(note.note_id)}
                onToggle={() => toggleNote(note.note_id)}
                onOpen={() => navigateToItem(note)}
                onDismiss={() => void handleDismiss(note.note_id)}
                onSnooze={() => void handleSnooze(note.note_id)}
                dispositionDisabled={isUpdating}
                failureMessage={bulkFailureMessages[note.note_id] ?? null}
              />
            ))}
          </Stack>
        </Box>
      )}

      {totalCount > 0 && (
        <Stack
          direction="row"
          spacing={1}
          sx={{ mt: 3, alignItems: 'center', justifyContent: 'center' }}
        >
          <IconButton
            aria-label="Previous page"
            disabled={!hasPreviousPage}
            onClick={() => setPage(page - 1)}
          >
            <ChevronLeft />
          </IconButton>
          <Typography sx={{ color: palette.muted, fontSize: 13 }}>
            Page {page} of {totalPages}
          </Typography>
          <IconButton
            aria-label="Next page"
            disabled={!hasNextPage}
            onClick={() => setPage(page + 1)}
          >
            <ChevronRight />
          </IconButton>
        </Stack>
      )}
    </Box>
  );
}
